/**
 * Creating a NEW library asset (CONTENT_MODEL_SPEC §2.6, §3.1, §3.5).
 *
 * A create is an **empty row followed by the ordinary edit**. `POST /assets`
 * with a title and no `File` makes the row and its (empty) folder; the bytes
 * then go in one at a time through `POST /assets/{id}/files`; the document
 * PATCHes last, alone, naming what came back. That is
 * {@link publishAssetFolder} — the very same delta write an edit does (§2.2)
 * — so the library has exactly one write path and a create is just the case
 * where the folder starts out empty.
 *
 * Why not one request. Storage names are minted by the **server** and by
 * nothing else (`assets/<slug>.<hash16>.<ext>`, §2.1), and a document write is
 * validated against what the folder holds *exactly as uploaded* — a
 * `files[k].uri` the folder does not hold is a 400 carrying
 * `[{ index, authored, stored }]`, never a rewrite, because *"a rewritten
 * document is no longer the bytes the client hashed."* So a one-shot
 * document-plus-`Files` create can only succeed when nothing new travels at
 * all, which for a create is never. The two shapes that used to paper over
 * that are both gone: the `.glz` bundle publish (the one path the server does
 * rewrite — a stopgap; a bundle is the download/import format, §4, not the
 * editor's write path) and promotion's document + `Files` multipart.
 *
 * Nothing here predicts a name, and nothing re-uploads a byte the folder
 * already holds.
 */

import type { EntityType, AssetInsertResponseDto } from 'trace-viewer/cms';
import { readBundleFolder, type SceneFolder } from 'trace-viewer/editor';
import {
    assetFileUploader,
    publishAssetFolder,
    type AssetDocumentPatch,
    type AssetFilesClient,
    type AssetFolderPublishSummary,
} from './asset-folder';
import type { AssetContentStorageField } from './asset-writes';
import { IS_FOLDER_STORAGE } from '../config/contentStorage';

// ── Where a new asset lands ─────────────────────────────────────────

/** The library folder a new asset is created in. */
export interface AssetFolderTarget {
    folderId: string;
    teamId: string;
}

/** Identity and trimmings a new row is created with. */
export interface NewAssetFolderInput {
    target: AssetFolderTarget;
    /** The row's title, and the name the document part travels under. */
    title: string;
    /**
     * What the row IS: `Trace` for a whole scene published from the content
     * panel, `Component` for a promoted subtree (SCENE_ROOT_SPEC §4).
     */
    entityType: EntityType;
    thumbnail?: File;
}

/**
 * The four calls a create makes, and nothing more.
 *
 * Narrow on purpose, like {@link AssetFilesClient}: the generated `Assets`
 * client satisfies it structurally, and a test can hand over four lines
 * instead of an HTTP stack.
 */
export interface AssetFolderCreateClient extends AssetFilesClient {
    /** `POST /assets` — the row. */
    assetsCreate(data: {
        FolderId?: string;
        TeamId?: string;
        File?: File;
        Thumbnail?: File;
        Title: string;
        EntityType?: EntityType;
    } & AssetContentStorageField): Promise<{ data?: AssetInsertResponseDto }>;
    /** `PATCH /assets/{assetId}` — the document, once the bytes are in. */
    assetsPartialUpdate(
        assetId: string,
        parts: AssetDocumentPatch & AssetContentStorageField,
    ): Promise<unknown>;
    /** `DELETE /assets/{assetId}` — only ever to clean up a failed create. */
    assetsDelete(assetId: string, data: { teamId: string }): Promise<unknown>;
}

// ── Step 1: the row ─────────────────────────────────────────────────

/**
 * Create the empty row, and answer its id.
 *
 * **No `File`.** The row exists so the folder has a name to live under; its
 * `Url` stays null until the document PATCH points it somewhere, and
 * `GET /assets/{id}/file` is a 404 in the seconds between. That is the whole
 * reason this is a separate call: `POST /assets/{id}/files` needs an asset id,
 * and the id is what the row is for.
 *
 * **`contentStorage: 'Folder'`, explicitly.** `withContentStorage` reads the
 * upload to decide, and there is no upload here to read — it would fall
 * through to the build's switch and could declare `Legacy` for a row that is
 * about to be a folder. A row created this way is always a folder, whatever
 * the build is set to, for the same reason a `.gltf` always is
 * (`asset-writes.ts`, §6.1).
 *
 * The thumbnail rides along here rather than on the PATCH: it is row data,
 * and the row is what this call makes.
 */
export async function createAssetFolder(
    client: AssetFolderCreateClient,
    input: NewAssetFolderInput,
): Promise<string> {
    const response = await client.assetsCreate({
        FolderId: input.target.folderId,
        TeamId: input.target.teamId,
        ...(input.thumbnail ? { Thumbnail: input.thumbnail } : {}),
        Title: input.title,
        EntityType: input.entityType,
        contentStorage: 'Folder',
    });

    const assetId = response.data?.assetId;
    if (!assetId) {
        throw new Error('The server created the asset but returned no id, so there is nowhere to put its content.');
    }
    return assetId;
}

// ── Step 1 + 2 + 3: the whole create ────────────────────────────────

/**
 * Create a library asset from a folder: empty row, then the delta publish.
 *
 * `folder.existing` is empty by construction for a new asset — the folder
 * holds nothing yet, so every relative entry has to travel as bytes. An
 * exporter that put entries there instead was told the wrong destination
 * (`destination: 'other'` is what makes a folder-loaded scene's files come
 * back as blobs, §3.5), and the document would name files the new folder does
 * not hold.
 *
 * @returns the new asset's id — the identity a reference node is minted from
 *   (`{apiBase}/assets/{id}/file`, §3.4) — and what the publish did.
 */
export async function publishNewAssetFolder(
    client: AssetFolderCreateClient,
    folder: SceneFolder,
    input: NewAssetFolderInput,
): Promise<{ assetId: string; summary: AssetFolderPublishSummary }> {
    const assetId = await createAssetFolder(client, input);

    try {
        const summary = await publishAssetFolder(
            {
                // A brand-new folder holds nothing, so nothing is recorded
                // and every file is sent.
                uploader: assetFileUploader(client, input.target.teamId),
                patch: (id, parts) => client.assetsPartialUpdate(id, parts),
            },
            () => Promise.resolve(folder),
            {
                assetId,
                teamId: input.target.teamId,
                documentName: `${input.title}.gltf`,
            },
        );
        return { assetId, summary };
    } catch (error) {
        await discardCreatedRow(client, assetId, input.target.teamId, error);
        throw error;
    }
}

/**
 * Create a library asset from an uploaded `.glz`: the bundle read as the
 * folder it packs (trace-viewer `readBundleFolder`), then
 * {@link publishNewAssetFolder}, so its behavior paths are repointed with its
 * files. Posting the bundle whole would leave them naming files the server's
 * import renamed, because that import repoints `files[]` only.
 *
 * @returns null, and makes no request, in a legacy-storage build (the server
 *   stores a bundle raw and renames nothing) and for a bundle with no
 *   `scene.gltf` (a legacy `scene.glxf`). The caller uploads either as it is.
 */
export async function publishBundleAsAssetFolder(
    client: AssetFolderCreateClient,
    bundle: Blob,
    input: NewAssetFolderInput,
): Promise<{ assetId: string; summary: AssetFolderPublishSummary } | null> {
    if (!IS_FOLDER_STORAGE) return null;
    const folder = await readBundleFolder(bundle);
    return folder ? publishNewAssetFolder(client, folder, input) : null;
}

/**
 * Take back the row when the content never made it.
 *
 * A create that fails after step 1 would otherwise leave a titled row in the
 * author's folder with nothing behind it — it lists, it opens, and it is
 * empty. Best effort: the original failure is what the author needs to see,
 * so a delete that fails too is logged and swallowed rather than replacing it.
 *
 * The uploaded blobs need no cleanup from here. A row that is gone leaves an
 * orphan folder, which the server's sweeper collects after its grace period.
 */
async function discardCreatedRow(
    client: AssetFolderCreateClient,
    assetId: string,
    teamId: string,
    cause: unknown,
): Promise<void> {
    console.error(`[CreateAsset] Publishing content into new asset ${assetId} failed; removing the empty row:`, cause);
    try {
        await client.assetsDelete(assetId, { teamId });
    } catch (deleteError) {
        console.error(
            `[CreateAsset] Could not remove asset ${assetId} after a failed create; `
            + 'it is in the folder with no content:',
            deleteError,
        );
    }
}
